/**
 * The onboarding wizard's tenant-level session lock (EDUS-2691 / US-12, FR-8).
 *
 * Only one Super Admin may configure a tenant at a time. The lock is ephemeral
 * coordination state — a Redis key whose TTL *is* the expiry rule — so once the
 * holder stops being active for {@link ONBOARDING_LOCK_TTL_MINUTES} it simply
 * ceases to exist and any qualified Super Admin may take over (FR-8.2).
 */

/**
 * Inactivity window before an abandoned lock disappears and can be taken over
 * (FR-8.2), i.e. the Redis key's TTL. Shared so the client's heartbeat interval
 * stays comfortably inside it.
 */
export const ONBOARDING_LOCK_TTL_MINUTES = 30;

/**
 * How often the wizard client pings to hold its lock open. Derived from the TTL
 * rather than written as its own literal so the two can never drift: shortening
 * the TTL without noticing a hard-coded interval would silently start dropping
 * admins mid-wizard. A sixth of the window tolerates several missed pings
 * (flaky network, a laptop briefly asleep) before the lock lapses.
 */
export const ONBOARDING_HEARTBEAT_INTERVAL_MS =
  (ONBOARDING_LOCK_TTL_MINUTES / 6) * 60_000;

/**
 * Who is configuring the tenant right now. There is deliberately no record of
 * *past* holders — the lock has no meaning once onboarding is done.
 */
export interface OnboardingLockHolder {
  email: string;
  /** Display name from their Google token; may be empty. */
  name: string;
  /**
   * ISO timestamp of when they FIRST took the lock — preserved across
   * refreshes, so this is "configuring since", not "last active".
   */
  since: string;
}

/**
 * The platform console's view: the holder plus when the lock lapses if nothing
 * refreshes it (read from the key's live TTL).
 *
 * An absolute instant rather than a "expires in N minutes" duration on purpose
 * — the console panel is a static fetch with a Refresh button, so a relative
 * countdown would freeze at fetch time and start lying immediately.
 */
export interface OnboardingActiveSession extends OnboardingLockHolder {
  /** ISO timestamp; null if the TTL couldn't be read. */
  expiresAt: string | null;
}
